import { useCallback, useEffect, useState } from 'preact/hooks';
import type { FeedDto, PlaylistSummaryDto } from '../../shared/types';
import { api, ApiError } from '../api';
import { Artwork } from '../components/Artwork';
import { Icon } from '../components/Icon';
import { LiveCarousel } from '../components/LiveCarousel';
import { OpmlManager } from '../components/OpmlManager';
import { SkeletonGrid } from '../components/Skeleton';
import { isSignedIn, sessionLoading } from '../state/session';

export function Library() {
  const [tab, setTab] = useState<'subscriptions' | 'playlists'>('subscriptions');
  const [feeds, setFeeds] = useState<(FeedDto & { subscribedAt: number })[]>([]);
  const [playlists, setPlaylists] = useState<PlaylistSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New playlist state
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadData = useCallback(() => {
    if (!isSignedIn.value) {
      setLoading(false);
      return () => {};
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([api.subscriptions(), api.playlists()])
      .then(([subRes, plRes]) => {
        if (!cancelled) {
          setFeeds(subRes.subscriptions);
          setPlaylists(plRes.playlists);
        }
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message || 'Failed to load library');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSignedIn.value]);

  useEffect(() => {
    if (sessionLoading.value) return;
    return loadData();
  }, [loadData, sessionLoading.value]);

  const handleCreatePlaylist = async (e: Event) => {
    e.preventDefault();
    const name = newPlaylistName.trim();
    if (!name) return;
    setCreateSaving(true);
    setCreateError(null);
    try {
      const res = await api.createPlaylist(name);
      setPlaylists((prev) => [
        ...prev,
        {
          id: res.playlist.id,
          name: res.playlist.name,
          createdAt: res.playlist.createdAt,
          itemCount: 0,
        },
      ]);
      setNewPlaylistName('');
      setCreatingPlaylist(false);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Failed to create playlist');
    } finally {
      setCreateSaving(false);
    }
  };

  if (sessionLoading.value) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Library</h1>
        </header>
        <SkeletonGrid count={6} label="Loading your library" />
      </div>
    );
  }

  if (!isSignedIn.value) {
    return (
      <div class="view">
        <h1>Library</h1>
        <p class="muted">Sign in to see your subscriptions and playlists.</p>
      </div>
    );
  }

  return (
    <div class="view">
      <header class="view__header">
        <h1>Library</h1>
        <div class="np__tabs" style={{ justifyContent: 'flex-start', margin: '1rem 0' }}>
          <button
            type="button"
            class={`np__tab ${tab === 'subscriptions' ? 'is-active' : ''}`}
            onClick={() => setTab('subscriptions')}
          >
            Subscriptions ({feeds.length})
          </button>
          <button
            type="button"
            class={`np__tab ${tab === 'playlists' ? 'is-active' : ''}`}
            onClick={() => setTab('playlists')}
          >
            Playlists ({playlists.length})
          </button>
        </div>
      </header>

      <LiveCarousel />

      {loading && <SkeletonGrid count={6} label="Loading your library" />}

      {error && !loading && (
        <div class="error" style={{ margin: '1rem 0' }}>
          <p>{error}</p>
          <button
            type="button"
            class="button button--ghost"
            style={{ marginTop: '0.5rem' }}
            onClick={() => loadData()}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && tab === 'subscriptions' && (
        <>
          {feeds.length === 0 ? (
            <div class="empty">
              <h2>No subscriptions yet</h2>
              <p class="muted">Find a podcast to follow or import your existing OPML subscriptions.</p>
              <div class="stack stack--row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <a class="button" href="/search">
                  Find podcasts
                </a>
              </div>
              <div style={{ marginTop: '1.5rem' }}>
                <OpmlManager onImportComplete={loadData} />
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <OpmlManager onImportComplete={loadData} />
              </div>
              <div class="podcast-grid">
                {feeds.map((feed) => (
                  <a key={feed.id} class="podcast-card" href={`/podcast/${feed.id}`}>
                    <Artwork src={feed.imageUrl} alt="" size={160} />
                    <span class="podcast-card__title">{feed.title}</span>
                    {feed.author && <span class="podcast-card__author">{feed.author}</span>}
                  </a>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {!loading && !error && tab === 'playlists' && (
        <div>
          <div class="stack stack--row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2>Playlists</h2>
            {!creatingPlaylist && (
              <button
                type="button"
                class="button button--primary"
                onClick={() => {
                  setCreateError(null);
                  setNewPlaylistName('');
                  setCreatingPlaylist(true);
                }}
              >
                + New Playlist
              </button>
            )}
          </div>

          {creatingPlaylist && (
            <form onSubmit={handleCreatePlaylist} class="panel" style={{ marginBottom: '1.5rem' }}>
              <h3>Create Playlist</h3>
              {createError && <p class="error">{createError}</p>}
              <div class="stack stack--row" style={{ gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="text"
                  class="input"
                  value={newPlaylistName}
                  onInput={(e) => setNewPlaylistName((e.target as HTMLInputElement).value)}
                  placeholder="Playlist name"
                  maxLength={120}
                  required
                  autoFocus
                />
                <button type="submit" class="button button--primary" disabled={createSaving}>
                  {createSaving ? 'Creating…' : 'Create'}
                </button>
                <button
                  type="button"
                  class="button button--ghost"
                  disabled={createSaving}
                  onClick={() => setCreatingPlaylist(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {playlists.length === 0 && !creatingPlaylist ? (
            <div class="empty">
              <h2>No playlists yet</h2>
              <p class="muted">Create custom playlists to organize your favorite podcast episodes.</p>
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  class="button button--primary"
                  onClick={() => setCreatingPlaylist(true)}
                >
                  Create your first playlist
                </button>
              </div>
            </div>
          ) : (
            <div class="playlist-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
              {playlists.map((pl) => (
                <a
                  key={pl.id}
                  class="panel"
                  href={`/playlist/${pl.id}`}
                  style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon name="queue" size={20} />
                    <strong style={{ fontSize: '1.1rem' }}>{pl.name}</strong>
                  </div>
                  <p class="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
                    {pl.itemCount} {pl.itemCount === 1 ? 'episode' : 'episodes'}
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
